import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useField, useFormikContext } from 'formik';
import Select from 'react-select';
import client from 'utils/client';
import TextInput from 'components/FormElements/TextInput';
import CustomSelect from 'components/FormElements/CustomSelect';
import MerchantPicker from 'components/FormElements/MerchantPicker';
import { BUSINESS_CATEGORY, SETTLEMENT_CATEGORY, SETTLEMENT_PROCESS } from 'shared';
import useSettings from 'hooks/useSettings';
import './terminal-form.css';

// Before→after override row: anchors the user in the default, shows what they're
// entering, and previews the effective value — all on one visual track.
const OverrideRow = ({
  label, name, baseLabel, effectiveLabel, unit, unitSide = 'right', help, step = '0.01',
}) => {
  const [field] = useField(name);
  return (
    <div className='override-row'>
      <div className='override-row-label'>{label}</div>
      <div className='override-track'>
        <div className='override-chip override-chip--default'>{baseLabel}</div>
        <div className='override-arrow' aria-hidden>→</div>
        <div className='override-input'>
          {unit && unitSide === 'left' && <span className='unit unit--left'>{unit}</span>}
          <input type='number' step={step} placeholder='0' {...field} />
          {unit && unitSide === 'right' && <span className='unit'>{unit}</span>}
        </div>
        <div className='override-arrow' aria-hidden>→</div>
        <div className='override-chip override-chip--effective'>{effectiveLabel}</div>
      </div>
      {help && <p className='override-help'>{help}</p>}
    </div>
  );
};

// NGN formatter for the defaults banner and effective-cap chip.
const fmtNaira = (n) => `₦${Number(n || 0).toLocaleString('en-NG', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const TransactionLimitInput = () => {
  const [field] = useField('transactionLimit');
  return (
    <div className='input-with-prefix'>
      <span className='prefix'>₦</span>
      <input
        id='transactionLimit'
        type='number'
        step='0.01'
        min='0'
        placeholder='e.g., 2000'
        {...field}
      />
    </div>
  );
};

// Naira end to end — stored in naira like the other fee columns.
const TransferChargeInput = () => {
  const [field] = useField('transferCharge');
  return (
    <div className='input-with-prefix'>
      <span className='prefix'>₦</span>
      <input
        id='transferCharge'
        type='number'
        step='0.01'
        min='0'
        placeholder='e.g., 50'
        {...field}
      />
    </div>
  );
};

const DEFAULT_BASE_FEE = 0.5;
const DEFAULT_BASE_CAP = 300;

const Section = ({ title, description, children }) => (
  <section className='form-section'>
    <header className='form-section-header'>
      <h4 className='form-section-title'>{title}</h4>
      {description && <p className='form-section-description'>{description}</p>}
    </header>
    <div className='form-grid'>{children}</div>
  </section>
);

const TerminalForm = ({ isEdit = false }) => {
  const { setFieldValue, values } = useFormikContext();
  const [providers, setProviders] = useState([]);

  useEffect(() => {
    client
      .get('/terminal-providers')
      .then((response) => setProviders(response.data.data || []))
      .catch(() => setProviders([]));
  }, []);

  const providerOptions = providers.map((p) => ({ label: p.provider_name, value: p.id }));
  const selectedProviderOption = providerOptions.find((o) => o.value === values.terminalProviderId) || null;
  const { getFeeConfig, loading: settingsLoading } = useSettings();
  const [feeDefaults, setFeeDefaults] = useState({ baseFee: '0', baseCap: '0' });
  const lastProviderRef = useRef(null);
  const lastCalculatedRef = useRef({ fee: null, cap: null });

  useEffect(() => {
    if (settingsLoading || !values.terminalProviderId) return;
    if (lastProviderRef.current === values.terminalProviderId) return;

    try {
      const feeConfig = getFeeConfig(values.terminalProviderId);
      if (feeConfig?.config) {
        const baseFee = feeConfig.config.transaction_fee || '0';
        const baseCap = feeConfig.config.transaction_fee_cap || '0';
        setFeeDefaults({ baseFee, baseCap });
        lastProviderRef.current = values.terminalProviderId;
      }
    } catch (error) {
      console.error('Error loading fee configuration:', error);
    }
  }, [getFeeConfig, settingsLoading, values.terminalProviderId]);

  useEffect(() => {
    if (values.settlementType === 'INSTANT' && values.settlementProcess && lastProviderRef.current) {
      setFieldValue('settlementProcess', '');
    }
  }, [values.settlementType, setFieldValue, values.settlementProcess]);

  const { calculatedFee, calculatedCap } = useMemo(() => {
    const baseFee = parseFloat(feeDefaults.baseFee) || DEFAULT_BASE_FEE;
    const baseCap = parseFloat(feeDefaults.baseCap) || DEFAULT_BASE_CAP;
    const feeRange = parseFloat(values.feeRange || 0);
    const capRange = parseFloat(values.capRange || 0);
    return {
      calculatedFee: (baseFee + feeRange).toFixed(2),
      calculatedCap: (baseCap + capRange).toFixed(2),
    };
  }, [feeDefaults.baseFee, feeDefaults.baseCap, values.feeRange, values.capRange]);

  useEffect(() => {
    if (!lastProviderRef.current) return;
    if (lastCalculatedRef.current.fee !== calculatedFee) {
      setFieldValue('transactionFee', calculatedFee);
      lastCalculatedRef.current.fee = calculatedFee;
    }
    if (lastCalculatedRef.current.cap !== calculatedCap) {
      setFieldValue('feeCap', calculatedCap);
      lastCalculatedRef.current.cap = calculatedCap;
    }
  }, [calculatedFee, calculatedCap, setFieldValue]);

  const isInstant = values.settlementType === 'INSTANT';

  return (
    <div className='form-wrapper'>
      <Section title='Linked Merchant' description='The merchant that owns this terminal.'>
        <div className='form-field-full'>
          <MerchantPicker name='merchantId' label='Merchant' required />
        </div>
      </Section>

      <Section title='Terminal Provider' description='The provider for this terminal.'>
        <div className='add-terminal-item form-field-full'>
          <label>
            Terminal Provider
            {isEdit && <span style={{ color: '#888', fontSize: 12, marginLeft: 6 }}>(cannot be changed)</span>}
          </label>
          <Select
            options={providerOptions}
            value={selectedProviderOption}
            onChange={(option) => {
              if (isEdit) return;
              setFieldValue('terminalProviderId', option ? option.value : '');
            }}
            placeholder='Select a terminal provider'
            isDisabled={isEdit}
            isClearable={!isEdit}
          />
        </div>
      </Section>

      <Section title='Device Identity' description='Physical identifiers printed on the POS device.'>
        <TextInput
          className='add-terminal-item form-field-sm'
          name='terminalId'
          label='Terminal ID'
          placeholder='20702YGP'
          required
        />
        <TextInput
          className='add-terminal-item form-field-md'
          name='terminalSerialNumber'
          label='Serial Number'
          placeholder='N300W1F6490'
          required
        />
        <CustomSelect
          className='add-terminal-item'
          id='businessSegment'
          name='businessSegment'
          label='Business Category'
          options={BUSINESS_CATEGORY}
          onChange={(option) => setFieldValue('businessSegment', option.value)}
          defaultInputValue={values.businessSegment}
          required
        />
        <TextInput
          className='add-terminal-item form-field-full'
          name='terminalLocation'
          label='Location'
          placeholder='e.g., 23 Emily Akinola str, Yaba'
          required
        />
      </Section>

      <Section title='Agent' description='Person responsible for operating this terminal.'>
        <TextInput
          className='add-terminal-item form-field-lg'
          name='terminalAgentName'
          label='Agent Name'
          placeholder='Full name'
          required
        />
        <TextInput
          className='add-terminal-item form-field-md'
          name='terminalAgentPhone'
          label='Agent Phone'
          placeholder='0801 234 5678'
          required
        />
      </Section>

      <Section title='Settlement' description='How and when funds reach the merchant.'>
        <CustomSelect
          className={`add-terminal-item ${isInstant ? 'form-field-full' : ''}`}
          id='settlementType'
          name='settlementType'
          label='Settlement Type'
          options={SETTLEMENT_CATEGORY}
          onChange={(option) => setFieldValue('settlementType', option.value)}
          defaultInputValue={values.settlementType}
          required
        />
        {!isInstant && (
          <CustomSelect
            className='add-terminal-item'
            id='settlementProcess'
            name='settlementProcess'
            label='Settlement Process'
            options={SETTLEMENT_PROCESS}
            onChange={(option) => setFieldValue('settlementProcess', option?.value || '')}
            defaultInputValue={values.settlementProcess}
            key={`settlement-process-${values.settlementType}`}
            required
            placeholder='Select settlement process'
          />
        )}
      </Section>

      <section className='form-section'>
        <header className='form-section-header'>
          <h4 className='form-section-title'>Fees & Limits</h4>
          <p className='form-section-description'>Leave adjustments at 0 to use the platform defaults.</p>
        </header>

        <div className='defaults-banner'>
          Platform default: <strong>{feeDefaults.baseFee}%</strong> fee, capped at <strong>{fmtNaira(feeDefaults.baseCap)}</strong>.
        </div>

        <OverrideRow
          label='Fee'
          name='feeRange'
          baseLabel={`${feeDefaults.baseFee}%`}
          effectiveLabel={`${calculatedFee}%`}
          unit='%'
          help='Signed adjustment from the default, e.g., -0.05 to charge less, 0.05 to charge more.'
        />

        <OverrideRow
          label='Cap'
          name='capRange'
          baseLabel={fmtNaira(feeDefaults.baseCap)}
          effectiveLabel={fmtNaira(calculatedCap)}
          unit='₦'
          unitSide='left'
          help='Signed naira adjustment to the per-transaction fee cap.'
          step='1'
        />

        <div className='add-terminal-item' style={{ marginTop: 16 }}>
          <label htmlFor='transactionLimit'>Transaction Limit</label>
          <TransactionLimitInput />
          <p className='form-field-help'>Maximum single-transaction amount this terminal can process. Leave blank for no limit.</p>
        </div>

        {isEdit && (
          <div className='add-terminal-item' style={{ marginTop: 16 }}>
            <label htmlFor='transferCharge'>Pay with Transfer Charge</label>
            <TransferChargeInput />
            <p className='form-field-help'>Flat fee debited from the merchant on every Pay with Transfer transaction. Leave blank for no charge.</p>
          </div>
        )}
      </section>
    </div>
  );
};

export default TerminalForm;
